import { OnboardingStatus } from '../graphql/generatedTypes';

/**
 * Mirrors colugo's `schema/Stores/utils/contractEndDate.ts`. A stored contract
 * end date is only meaningful when it is present, parseable, and not an epoch
 * sentinel — legacy rows hold "1970-01-01" (or an epoch timestamp) because a
 * cleared date used to serialize to `new Date(0)` instead of null.
 *
 * Kept in sync with the backend deliberately: the backend is the authority and
 * rejects a bad save, but the UI needs the same answer up front to disable
 * Terminated instead of letting the operator hit an error.
 */
const SENTINEL_MAX_DATE_PART = '1970-01-01';

// True only for a date part that names the day it claims. `new Date` rolls an
// impossible calendar day over rather than rejecting it — '2026-02-30' parses
// as 2026-03-02, in the `T00:00:00Z` form built here as much as in any other.
function namesARealDay(datePart: string): boolean {
  const asUtcMidnight = new Date(`${datePart}T00:00:00Z`);
  return (
    !Number.isNaN(asUtcMidnight.getTime()) &&
    asUtcMidnight.toISOString().slice(0, 10) === datePart
  );
}

export function hasMeaningfulContractEndDate(
  value: string | null | undefined,
): boolean {
  if (value == null || value === '') return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  const datePart = /^\d{4}-\d{2}-\d{2}/.test(value)
    ? value.slice(0, 10)
    : parsed.toISOString().slice(0, 10);
  if (!namesARealDay(datePart)) return false;
  return datePart > SENTINEL_MAX_DATE_PART;
}

export function isTerminated(
  status: OnboardingStatus | string | null | undefined,
): boolean {
  return status === OnboardingStatus.Terminated;
}
